import React from 'react';
import { Check, ArrowRight, Crown, Sparkles, Zap, Link as LinkIcon, Clock, Star, User, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const LeadershipLegacyProgram = () => {
    return (
        <div className="bg-exiser-light min-h-screen">
            {/* Hero */}
            <div className="bg-gradient-to-br from-exiser-primary to-purple-900 text-white py-16 md:py-20 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
                <div className="max-w-5xl mx-auto px-4 relative z-10">
                    <Link to="/categories" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 uppercase tracking-wider text-xs font-bold transition-colors">
                        <ArrowLeft className="w-4 h-4" /> Back to Programmes
                    </Link>
                    <div className="inline-flex items-center gap-2 bg-exiser-accent/20 border border-exiser-accent/60 px-3 py-1 rounded-full mb-4">
                        <Crown className="w-4 h-4 text-exiser-accent" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-exiser-accent">
                            Premium | Invitation / Application Only
                        </span>
                    </div>
                    <div className="mb-2 text-exiser-accent font-bold uppercase tracking-widest text-xs">
                        Elite Leadership Program
                    </div>
                    <h1 className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-4">
                        Leadership Legacy Program
                    </h1>
                    <p className="text-sm md:text-base text-purple-100 max-w-3xl font-sans font-medium italic border-l-4 border-exiser-accent pl-4 py-1 mb-6">
                        "This is not a course. This is a leadership, mentorship & legacy-building journey."
                    </p>
                    <p className="text-sm md:text-base text-purple-100 max-w-3xl font-sans leading-relaxed">
                        A 12-month ecosystem designed to: <br />
                        <span className="font-bold text-white">Rebuild identity → Engineer performance → Build wealth → Create legacy.</span>
                    </p>

                    <div className="mt-8 flex flex-wrap items-center gap-6 text-xs uppercase tracking-[0.15em] font-bold text-purple-200">
                        <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-exiser-accent" />
                            <span>12 Months</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-exiser-accent" />
                            <span>Business Associates | Sales Managers | Future Leaders</span>
                        </div>
                    </div>

                    <div className="mt-10 flex flex-wrap gap-3">
                        <Link
                            to="/categories#leadership-legacy-program"
                            className="inline-flex items-center gap-2 bg-exiser-accent text-exiser-primary px-8 py-4 text-xs font-bold uppercase tracking-[0.22em] rounded-lg hover:bg-white shadow-lg transition-transform hover:-translate-y-1"
                        >
                            Request Application
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </div>

            {/* Content Container */}
            <div className="max-w-5xl mx-auto px-4 py-12 md:py-16 space-y-16">

                {/* 1. Why This Exists */}
                <section className="grid md:grid-cols-2 gap-8 items-stretch">
                    <div className="bg-white rounded-2xl shadow-xl border border-purple-100 p-8 flex flex-col justify-center">
                        <h2 className="text-xl md:text-2xl font-bold uppercase text-exiser-primary mb-6 flex items-center gap-3">
                            <Zap className="w-6 h-6 text-exiser-accent fill-current" />
                            Why This Program Exists
                        </h2>
                        <p className="text-gray-500 font-bold uppercase tracking-wider text-xs mb-4">Most leaders:</p>
                        <ul className="space-y-4 text-sm text-gray-700 font-sans">
                            <li className="flex gap-3">
                                <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 shrink-0" />
                                <span>Learn skills but never <span className="font-bold text-red-600">sustain growth</span></span>
                            </li>
                            <li className="flex gap-3">
                                <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 shrink-0" />
                                <span>Build teams but never create <span className="font-bold text-red-600">legacy</span></span>
                            </li>
                            <li className="flex gap-3">
                                <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 shrink-0" />
                                <span>Earn money but lack <span className="font-bold text-red-600">long-term wealth systems</span></span>
                            </li>
                            <li className="flex gap-3">
                                <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 shrink-0" />
                                <span>Lead teams but don’t build <span className="font-bold text-red-600">leaders after them</span></span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-gradient-to-br from-gray-900 to-exiser-primary text-white p-8 rounded-2xl shadow-xl flex flex-col justify-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-10 -mt-10"></div>
                        <h3 className="text-lg font-bold uppercase mb-6 flex items-center gap-2">
                            The 1-Year Transformation Promise
                        </h3>
                        <ul className="space-y-3">
                            {[
                                "Think, act, and decide like elite leaders",
                                "Run self-sustaining high-performance teams",
                                "Build predictable income & long-term wealth",
                                "Become respected mentors & industry icons",
                                "Operate with emotional mastery & clarity",
                                "Leave behind systems, not just results"
                            ].map((item, idx) => (
                                <li key={idx} className="flex gap-3 text-sm text-gray-200">
                                    <Check className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                {/* 2. Program Positioning - Very Important */}
                <section className="bg-yellow-50 border border-yellow-200 rounded-2xl p-8 text-center">
                    <h2 className="text-lg md:text-xl font-bold uppercase text-yellow-800 mb-6 flex items-center justify-center gap-2">
                        <TrophyIcon className="w-5 h-5" />
                        Program Positioning
                    </h2>
                    <div className="flex flex-wrap justify-center gap-4">
                        {[
                            "Application & Screening Only",
                            "Limited Seats",
                            "Personal Mentorship Access",
                            "Confidential Leadership Circle",
                            "Recognition & Reward"
                        ].map((pos, i) => (
                            <span key={i} className="bg-white border border-yellow-100 text-yellow-900 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider shadow-sm">
                                {pos}
                            </span>
                        ))}
                    </div>
                </section>

                {/* 3. What's Included */}
                <section className="rounded-2xl bg-white border border-purple-100 shadow-xl overflow-hidden">
                    <div className="bg-exiser-primary p-6 text-white text-center">
                        <h2 className="text-xl md:text-2xl font-bold uppercase flex items-center justify-center gap-2">
                            <LinkIcon className="w-6 h-6 text-exiser-accent" />
                            What’s Included (12-Month Ecosystem)
                        </h2>
                    </div>
                    <div className="p-8 md:p-12">
                        <div className="grid md:grid-cols-2 gap-8 items-center">
                            <div className="space-y-4">
                                <h4 className="font-bold text-exiser-primary uppercase text-sm tracking-widest border-b border-gray-100 pb-2">Includes BOTH Previous Programs:</h4>
                                <ul className="space-y-3 text-sm font-sans text-gray-700">
                                    <li className="flex items-center gap-3 bg-purple-50 p-3 rounded-lg"><Check className="w-5 h-5 text-exiser-primary" /> <strong>High-Performance Leadership</strong> (2 Days)</li>
                                    <li className="flex items-center gap-3 bg-purple-50 p-3 rounded-lg"><Check className="w-5 h-5 text-exiser-primary" /> <strong>Advance Leadership Transformation Mastery</strong> (3 Months)</li>
                                </ul>
                            </div>
                            <div className="flex flex-col items-center justify-center text-center p-6 bg-gray-50 rounded-xl border border-gray-100">
                                <div className="w-16 h-16 bg-exiser-accent rounded-full flex items-center justify-center text-exiser-primary mb-4 shadow-md">
                                    <Sparkles className="w-8 h-8" />
                                </div>
                                <p className="text-lg font-bold uppercase tracking-widest text-exiser-primary">
                                    Plus Deep 1-Year Mentorship <br /> <span className="text-sm text-gray-500 font-normal">& Transformation Systems</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 4. The Ecosystem */}
                <section className="space-y-8">
                    <div className="text-center">
                        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-gray-400">Holistic Growth</span>
                        <h2 className="text-2xl md:text-3xl font-bold uppercase text-exiser-primary mt-2">
                            The 1-Year Leadership Ecosystem
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <SystemCard
                            title="Weekly Systems"
                            subtitle="Core Engine"
                            color="blue"
                            items={[
                                "Elite Coaching (Leadership + Business)",
                                "Growth Psychology & Identity Sessions",
                                "Performance Audit & Accountability",
                                "Recruitment & Team Health Review"
                            ]}
                        />
                        <SystemCard
                            title="Monthly Systems"
                            subtitle="Scaling Engine"
                            color="green"
                            items={[
                                "Business Growth Review",
                                "Team & Leader Development Plan",
                                "Reward & Recognition Framework",
                                "Wealth & Money Psychology"
                            ]}
                        />
                        <SystemCard
                            title="Quarterly Systems"
                            subtitle="Expansion Engine"
                            color="orange"
                            items={[
                                "90-Day Leadership & Income Planning",
                                "Team Scaling & Succession Review",
                                "Advanced Problem-Solving Labs",
                                "Leadership Authority & Influence Upgrade"
                            ]}
                        />
                        <SystemCard
                            title="Annual Systems"
                            subtitle="Legacy Engine"
                            color="red"
                            items={[
                                "Vision & Legacy Blueprint",
                                "Wealth Continuity System",
                                "Next-Gen Leader Creation",
                                "Public Recognition & Certification"
                            ]}
                        />
                    </div>
                </section>

                {/* 5. The 12-Month Curriculum */}
                <section className="bg-gray-50 rounded-3xl p-8 md:p-12 border border-gray-200">
                    <div className="text-center mb-10">
                        <h2 className="text-2xl md:text-3xl font-bold uppercase text-gray-900">
                            The 12-Month Master Curriculum
                        </h2>
                        <p className="text-sm text-gray-500 mt-2 uppercase tracking-widest">A Phased Approach to Legacy</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        <PillarCard
                            phase="Phase 1"
                            title="Identity & Foundation"
                            duration="Months 1–3"
                            outcome="Leader Reborn"
                            color="blue"
                            points={[
                                "Elite Leadership Identity Engineering",
                                "Emotional Intelligence & Stability",
                                "High-Performance Habits",
                                "Core Recruitment Systems",
                                "Team Activation & Momentum"
                            ]}
                        />
                        <PillarCard
                            phase="Phase 2"
                            title="Performance & Scale"
                            duration="Months 4–6"
                            outcome="Leader in Control"
                            color="green"
                            points={[
                                "Advanced Team Handling",
                                "Predictable Recruitment Systems",
                                "Sales Leadership Psychology",
                                "Digital Authority & Brand Building",
                                "Super Retention Formula"
                            ]}
                        />
                        <PillarCard
                            phase="Phase 3"
                            title="Wealth & Authority"
                            duration="Months 7–9"
                            outcome="Leader of Influence"
                            color="orange"
                            points={[
                                "Income Expansion Systems",
                                "Wealth Psychology & Asset Thinking",
                                "Influence, Negotiation & Power Dynamics",
                                "Recognition Strategy",
                                "Industry Positioning"
                            ]}
                        />
                        <PillarCard
                            phase="Phase 4"
                            title="Legacy & Immortality"
                            duration="Months 10–12"
                            outcome="Legacy Builder"
                            color="red"
                            points={[
                                "Leadership Succession System",
                                "Mentorship & Culture Engineering",
                                "Long-Term Wealth Continuity",
                                "Public Authority & Recognition",
                                "Final Leadership Thesis"
                            ]}
                        />
                    </div>
                </section>


                {/* 6. Signature Experiences */}
                <section className="bg-white rounded-2xl shadow-lg border border-exiser-accent/40 p-8 text-center relative overflow-hidden group">
                    <div className="absolute inset-0 bg-exiser-accent/5 group-hover:bg-exiser-accent/10 transition-colors" />
                    <h2 className="text-xl md:text-2xl font-bold uppercase text-exiser-primary mb-8 flex items-center justify-center gap-2 relative z-10">
                        <Crown className="w-6 h-6 text-exiser-accent" />
                        Signature Experiences (Exclusive)
                    </h2>
                    <div className="grid md:grid-cols-3 gap-6 font-sans text-sm font-medium text-gray-700 relative z-10">
                        {[
                            "Private Leadership Council (Monthly)",
                            "Closed-Door Psychology Intensives",
                            "One-to-One Strategic Reviews (Quarterly)",
                            "Elite Recognition & Awards Ceremony",
                            "Lifetime Leadership Circle Access",
                            "“Legacy Leader” Certification"
                        ].map((exp, idx) => (
                            <div key={idx} className="bg-white p-4 rounded-xl shadow-sm flex items-center justify-center gap-3 border border-purple-100 hover:-translate-y-1 transition-transform">
                                <Star className="w-4 h-4 text-exiser-primary fill-current" />
                                <span>{exp}</span>
                            </div>
                        ))}
                    </div>
                </section>

                {/* 7. CTA & Who Should Apply */}
                <section className="bg-exiser-primary rounded-2xl p-8 md:p-14 text-white text-center space-y-8 relative overflow-hidden">
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-20" />
                    <div className="relative z-10">
                        <h3 className="text-2xl md:text-4xl font-bold uppercase mb-6">
                            Who Should Apply?
                        </h3>
                        <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto mb-8">
                            {["Business Associates", "Sales Managers", "Agency Associates", "High-Potential Leaders", "Future Industry Icons"].map((role, i) => (
                                <span key={i} className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-xs md:text-sm font-bold uppercase tracking-wider backdrop-blur-md border border-white/20">
                                    <Check className="w-4 h-4 text-exiser-accent" /> {role}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="bg-black/30 p-6 rounded-2xl max-w-2xl mx-auto backdrop-blur-md border border-white/10 relative z-10">
                        <p className="text-base md:text-lg font-sans italic text-gray-200 leading-relaxed">
                            "If someone wants <span className="text-white font-bold not-italic">quick results</span>, this is not for them. <br className="hidden md:block" />
                            If someone wants <span className="text-exiser-accent font-bold not-italic">life & leadership transformation</span>, this is."
                        </p>
                    </div>

                    <div className="relative z-10 pt-4">
                        <Link
                            to="/categories#leadership-legacy-program"
                            className="inline-flex items-center gap-2 bg-exiser-accent text-exiser-primary px-10 py-5 text-sm font-bold uppercase tracking-[0.25em] rounded-lg hover:bg-white hover:scale-105 transition-all shadow-xl"
                        >
                            Request Application
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                    </div>
                </section>

            </div>
        </div>
    );
};

// --- Sub Components ---

const TrophyIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" /></svg>
);


const PillarCard = ({ phase, title, duration, outcome, points, color }) => {
    const colorClasses = {
        blue: "border-blue-200 bg-blue-50",
        green: "border-green-200 bg-green-50",
        orange: "border-orange-200 bg-orange-50",
        red: "border-red-200 bg-red-50",
    };
    const headerColors = {
        blue: "text-blue-700 bg-blue-100",
        green: "text-green-700 bg-green-100",
        orange: "text-orange-700 bg-orange-100",
        red: "text-red-700 bg-red-100"
    };

    return (
        <div className={`rounded-2xl border p-6 flex flex-col h-full ${colorClasses[color] || "border-gray-200"} hover:shadow-lg transition-shadow bg-white`}>
            <div className="mb-6">
                <div className="flex justify-between items-start mb-3">
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full ${headerColors[color]}`}>{phase}</span>
                    <span className="text-[10px] font-bold uppercase text-gray-400 border border-gray-100 px-2 py-1 rounded">{duration}</span>
                </div>
                <h3 className="text-xl font-bold uppercase text-gray-900 mt-2 leading-tight">{title}</h3>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mt-2">Outcome: <span className="text-exiser-primary">{outcome}</span></p>
            </div>
            <ul className="space-y-3 text-xs text-gray-700 font-sans mt-auto border-t border-gray-100 pt-4">
                {points.map((p, idx) => (
                    <li key={idx} className="flex gap-2.5 items-start">
                        <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${color === 'blue' ? 'bg-blue-400' : color === 'green' ? 'bg-green-400' : color === 'orange' ? 'bg-orange-400' : 'bg-red-400'}`} />
                        <span className="leading-relaxed">{p}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const SystemCard = ({ title, subtitle, items, color }) => {
    const borders = {
        blue: "border-l-4 border-l-blue-500",
        green: "border-l-4 border-l-green-500",
        orange: "border-l-4 border-l-orange-500",
        red: "border-l-4 border-l-red-500"
    };

    return (
        <div className={`bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow ${borders[color]}`}>
            <div className="mb-4 border-b border-gray-100 pb-3">
                <h4 className="font-bold text-gray-900 uppercase text-sm">{title}</h4>
                <p className="text-[10px] text-gray-400 uppercase tracking-widest">{subtitle}</p>
            </div>
            <ul className="space-y-2">
                {items.map((item, i) => (
                    <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                        <Check className="w-3.5 h-3.5 text-exiser-primary mt-0.5 shrink-0" />
                        <span className="leading-snug">{item}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}


export default LeadershipLegacyProgram;
